import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    GitMerge, RefreshCw, AlertCircle, CheckCircle, Search, ChevronDown, ChevronUp,
} from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import {
    getCannibalizationAnalysis,
    analyzeCannibalization,
    CannibalizationAnalysis,
    CannibalizationSplitCandidate,
    CannibalizationConsolidateCandidate,
} from '../../services/api';
import './CannibalizationPanel.css';

interface CannibalizationPanelProps {
    projectId: string;
}

/** Show the URL path only (more scannable than the full origin). */
const displayPage = (page: string): string => {
    try {
        const u = new URL(page);
        return (u.pathname || '/') + (u.search || '');
    } catch {
        return page;
    }
};

/** Format a UTC ISO-Z string as a relative time label. */
const relativeTime = (isoZ: string | null): string => {
    if (!isoZ) return '';
    try {
        const d = new Date(isoZ);
        const diffMs = Date.now() - d.getTime();
        const mins = Math.floor(diffMs / 60000);
        if (mins < 2) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    } catch {
        return '';
    }
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SplitCandidateRow: React.FC<{
    candidate: CannibalizationSplitCandidate;
    onSendToPlanner: (candidate: CannibalizationSplitCandidate) => void;
}> = ({ candidate, onSendToPlanner }) => (
    <tr key={candidate.page}>
        <td>
            <div className="lcp-page-cell" title={candidate.page}>
                {displayPage(candidate.page)}
            </div>
            {candidate.task_name && (
                <div className="lcp-task-name" title={candidate.task_name}>
                    Article: {candidate.task_name}
                </div>
            )}
        </td>
        <td style={{ textAlign: 'right', fontWeight: 600 }}>
            {candidate.total_impressions.toLocaleString()}
        </td>
        <td>
            <div className="canb-cluster-chips">
                {candidate.clusters.map((c, i) => (
                    <span key={i} className="canb-cluster-chip" title={`${c.intent} · ${c.impressions} impressions`}>
                        {c.label}
                    </span>
                ))}
            </div>
        </td>
        <td className="lcp-action-cell">
            <GlassButton
                size="sm"
                variant="primary"
                pill
                onClick={() => onSendToPlanner(candidate)}
                title="Pre-fill the 30-day content planner with these split seeds"
            >
                Send to planner
            </GlassButton>
        </td>
    </tr>
);

const ConsolidateCandidateRow: React.FC<{
    candidate: CannibalizationConsolidateCandidate;
}> = ({ candidate }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <tr key={candidate.query}>
            <td style={{ fontWeight: 600, color: 'var(--text)' }}>{candidate.query}</td>
            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                {candidate.total_impressions.toLocaleString()}
            </td>
            <td>
                <div className="canb-competing-pages">
                    <button
                        className="canb-competing-pages-toggle"
                        onClick={() => setExpanded(v => !v)}
                    >
                        {candidate.pages.length} pages
                        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                    {expanded && (
                        <div className="canb-competing-page-list">
                            {candidate.pages.map((p, i) => (
                                <div key={i} className="canb-competing-page-row">
                                    <span className="canb-competing-page-path" title={p.page}>
                                        {displayPage(p.page)}
                                    </span>
                                    <span>{p.impressions.toLocaleString()} impr</span>
                                    {p.position != null && (
                                        <span>pos {p.position.toFixed(1)}</span>
                                    )}
                                    {p.task_name && (
                                        <span className="canb-task-link">({p.task_name})</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </td>
            <td className="lcp-action-cell">
                <span className="canb-advisory">Pick strongest, 301 others</span>
            </td>
        </tr>
    );
};

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

const CannibalizationPanel: React.FC<CannibalizationPanelProps> = ({ projectId }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<CannibalizationAnalysis | null>(null);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchCached = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getCannibalizationAnalysis(projectId);
            setData(result);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to load analysis.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchCached();
    }, [fetchCached]);

    const handleRunAnalysis = async (force = true) => {
        setAnalyzing(true);
        setError(null);
        try {
            const result = await analyzeCannibalization(projectId, { force });
            setData(result);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Analysis failed — please try again.');
        } finally {
            setAnalyzing(false);
        }
    };

    const handleSendToPlanner = (candidate: CannibalizationSplitCandidate) => {
        sessionStorage.setItem(
            'aime_planner_prefill',
            JSON.stringify({
                projectId,
                seeds: candidate.suggested_seeds,
                source: 'cannibalization',
            }),
        );
        navigate('/content?openMasterplan=true');
    };

    const status = data?.status ?? null;
    const splitCandidates = data?.split_candidates ?? [];
    const consolidateCandidates = data?.consolidate_candidates ?? [];

    // ---------------------------------------------------------------------------
    // Render: loading skeleton
    // ---------------------------------------------------------------------------
    if (loading) {
        return (
            <GlassCard className="lcp-panel">
                <div className="lcp-loading">
                    <RefreshCw size={16} className="lcp-spinning" />
                    <span>Loading analysis…</span>
                </div>
            </GlassCard>
        );
    }

    return (
        <GlassCard className="lcp-panel">
            {/* Header */}
            <div className="lcp-header">
                <div className="lcp-title">
                    <GitMerge size={16} />
                    <span>Keyword Cannibalization</span>
                </div>
                <div className="lcp-header-right">
                    {data?.computed_at && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                            Last analysed {relativeTime(data.computed_at)}
                        </span>
                    )}
                    <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={fetchCached}
                        disabled={loading || analyzing}
                        title="Reload cached result"
                        icon={<RefreshCw size={16} className={(loading || analyzing) ? 'lcp-spinning' : ''} />}
                    />
                    {status !== 'not_run' && (
                        <GlassButton
                            size="sm"
                            variant="primary"
                            pill
                            disabled={analyzing}
                            loading={analyzing}
                            onClick={() => handleRunAnalysis(true)}
                            title="Re-run the analysis with the latest GSC data"
                        >
                            {analyzing ? 'Analysing…' : 'Re-run'}
                        </GlassButton>
                    )}
                </div>
            </div>
            <p className="lcp-subtitle">
                Surface pages splitting authority across multiple topics (split) and queries
                where your own pages compete with each other (consolidate).
            </p>

            {/* Error */}
            {error && (
                <div className="lcp-error">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {/* State: analyzing spinner */}
            {analyzing && (
                <div className="lcp-loading">
                    <RefreshCw size={16} className="lcp-spinning" />
                    <span>Analysing — clustering your ranking queries (this can take a minute)…</span>
                </div>
            )}

            {/* State: not_run */}
            {!analyzing && status === 'not_run' && (
                <div className="lcp-empty">
                    <GitMerge size={32} className="lcp-empty-icon" />
                    <p>No analysis has been run yet.</p>
                    <p className="lcp-empty-hint">
                        AIME will cluster your ranking queries across all pages to find split and consolidation opportunities.
                    </p>
                    <div style={{ marginTop: 16 }}>
                        <GlassButton
                            variant="primary"
                            pill
                            onClick={() => handleRunAnalysis(false)}
                            disabled={analyzing}
                            loading={analyzing}
                        >
                            Run analysis
                        </GlassButton>
                    </div>
                </div>
            )}

            {/* State: insufficient_data */}
            {!analyzing && status === 'insufficient_data' && (
                <div className="lcp-empty">
                    <Search size={32} className="lcp-empty-icon" />
                    <p>Not enough Search Console data yet.</p>
                    {data?.data_floor && (
                        <p className="lcp-empty-hint">
                            Found {data.data_floor.distinct_pages ?? 0} pages, {data.data_floor.distinct_queries ?? 0} queries,{' '}
                            {(data.data_floor.total_impressions ?? 0).toLocaleString()} impressions.
                            Need at least {data.data_floor.required_pages} pages, {data.data_floor.required_queries} queries,
                            and {data.data_floor.required_impressions?.toLocaleString()} impressions.
                        </p>
                    )}
                    <p className="lcp-empty-hint" style={{ marginTop: 6 }}>
                        Connect Google Search Console (Performance tab) and keep publishing to build up data.
                    </p>
                </div>
            )}

            {/* State: ok, zero candidates */}
            {!analyzing && status === 'ok' && splitCandidates.length === 0 && consolidateCandidates.length === 0 && (
                <div className="lcp-empty">
                    <CheckCircle size={32} className="lcp-empty-icon lcp-empty-icon--ok" />
                    <p>No cannibalization found — your pages target distinct topics.</p>
                </div>
            )}

            {/* State: ok, results */}
            {!analyzing && status === 'ok' && (splitCandidates.length > 0 || consolidateCandidates.length > 0) && (
                <>
                    {data?.truncated && (
                        <div className="canb-truncated-note">
                            {data.truncated_message}
                        </div>
                    )}

                    {splitCandidates.length > 0 && (
                        <>
                            <h4 className="canb-section-heading">
                                <GitMerge size={14} />
                                Pages to split ({splitCandidates.length})
                            </h4>
                            <table className="seo-keyword-table">
                                <thead>
                                    <tr>
                                        <th>Page</th>
                                        <th style={{ textAlign: 'right' }}>Impressions</th>
                                        <th>Topic Clusters</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {splitCandidates.map((c, i) => (
                                        <SplitCandidateRow
                                            key={i}
                                            candidate={c}
                                            onSendToPlanner={handleSendToPlanner}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {consolidateCandidates.length > 0 && (
                        <>
                            <h4 className="canb-section-heading">
                                <AlertCircle size={14} />
                                Queries to consolidate ({consolidateCandidates.length})
                            </h4>
                            <table className="seo-keyword-table">
                                <thead>
                                    <tr>
                                        <th>Query</th>
                                        <th style={{ textAlign: 'right' }}>Impressions</th>
                                        <th>Competing Pages</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {consolidateCandidates.map((c, i) => (
                                        <ConsolidateCandidateRow key={i} candidate={c} />
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </>
            )}
        </GlassCard>
    );
};

export default CannibalizationPanel;
